import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useContext, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ActionHeadTitle,
  ChangePasswordModal,
  DetailsCard,
  MainLayout,
  ProfilePageTemplate,
  Put,
  Text,
  UserStatusLabel
} from 'components';
import { NotificationContext, ThemeContext, UserContext } from 'contexts';
import { Divider, Space } from 'antd';
import { BarChart, Refresh02, ShortLeft, UserCircle } from 'icons';
import { dateTimeRowFormat, format, getNotificationError, toGBDecrease, isLogged } from 'utils';
import { users, usersApi } from 'api';
import { ClientUserProfile } from 'api/users.types';

// Type guard to check if user data is ClientUserProfile
const isClientUserProfile = (user: any): user is ClientUserProfile => {
  return user && 'cif' in user && 'department' in user && 'storage_limit' in user;
};

export const MyProfilePage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: loggedUser } = useContext(UserContext);
  const { currentTheme } = useContext(ThemeContext);
  const { notification } = useContext(NotificationContext);

  // Directly fetch client details to ensure we have all the data
  const { data: clientDetailsData, isLoading: isLoadingClientDetails, error: clientDetailsError } = useQuery({
    ...usersApi.getClientDetails(),
    enabled: isLogged()
  });

  // Use directly fetched data if available, otherwise fall back to UserContext data
  const userData = clientDetailsData || (isClientUserProfile(loggedUser) ? loggedUser : null);

  // Debug: log data to console
  console.log('MyProfilePage - clientDetailsData:', clientDetailsData);
  console.log('MyProfilePage - loggedUser:', loggedUser);
  console.log('MyProfilePage - userData:', userData);
  console.log('MyProfilePage - isLoadingClientDetails:', isLoadingClientDetails);
  console.log('MyProfilePage - clientDetailsError:', clientDetailsError);

  const [showResetPassModal, setShowResetPassModal] = useState(false);

  const updateMutate = useMutation(
    (data: FormData) =>
      loggedUser?.is_organization_administrator
        ? users.updateClient(userData?.id || 0, data)
        : users.updateProfilePartner(data),

    {
      onSuccess: () => {
        queryClient.invalidateQueries(usersApi.getLoggedUserData().queryKey);
        queryClient.invalidateQueries(usersApi.getClientDetails().queryKey);
      },
      onError: (err: AxiosError) => {
        notification.error({ message: getNotificationError(err) });
      }
    }
  );

  // Show loading only if we don't have user data yet and we're still loading
  if (isLoadingClientDetails && !userData) {
    return (
      <MainLayout pageHeading={<ActionHeadTitle Icon={ShortLeft} label={t('myProfile')} />}>
        <div>Loading...</div>
      </MainLayout>
    );
  }

  // Show error if request failed
  if (clientDetailsError && !userData) {
    return (
      <MainLayout pageHeading={<ActionHeadTitle Icon={ShortLeft} label={t('myProfile')} />}>
        <div>Error loading profile data</div>
      </MainLayout>
    );
  }

  // If we still don't have data, show message
  if (!userData) {
    return (
      <MainLayout pageHeading={<ActionHeadTitle Icon={ShortLeft} label={t('myProfile')} />}>
        <div>No profile data available</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout pageHeading={<ActionHeadTitle Icon={ShortLeft} label={t('myProfile')} />}>
      <ProfilePageTemplate
        data={userData}
        mutation={updateMutate}
        footer={
          <Space
            style={{
              borderTop: `.5px solid ${currentTheme['grey-70']}`,
              width: 'calc(100% - 64px)',
              justifyContent: 'space-between',
              padding: '18px 32px'
            }}
          >
            <Space size={16} styles={{ item: { display: 'flex' } }}>
              <Text size="lg" strong>
                {t('password')}
              </Text>
            </Space>
            <Space
              size={8}
              styles={{ item: { display: 'flex' } }}
              style={{ color: currentTheme['purple-100'], cursor: 'pointer' }}
              onClick={() => setShowResetPassModal(true)}
            >
              <Text size="lg">{t('requestChangePassword')}</Text>
              <Refresh02 />
            </Space>
          </Space>
        }
      >
        <DetailsCard
          title={t('clientDetails')}
          Icon={UserCircle}
          items={[
            { label: t('companyCif'), value: <Put>{userData?.cif}</Put> },
            {
              label: t('department'),
              value: <Put>{userData?.department}</Put>
            },
            { label: t('emailAddress'), value: <Put>{userData?.email}</Put> },
            {
              label: t('domainPrefix'),
              value: (
                <Put condition={!!userData?.domain_prefix}>
                  <Text code>{userData?.domain_prefix}</Text>
                </Put>
              )
            }
          ]}
        />

        <Divider type="vertical" style={{ height: 'auto', margin: 0 }} />

        <DetailsCard
          title={t('activity')}
          Icon={BarChart}
          items={[
            {
              label: t('profileStatus'),
              value: <UserStatusLabel unfilled status={userData?.state} />
            },
            {
              label: t('profileCreatedAt'),
              value: userData?.created_at && format(userData.created_at, dateTimeRowFormat)
            },
            {
              label: t('lastActivity'),
              value: userData?.last_login && format(userData.last_login, dateTimeRowFormat)
            },
            {
              label: t('storageSpace'),
              value: toGBDecrease(userData?.storage_limit)
            }
          ]}
        />
      </ProfilePageTemplate>

      <ChangePasswordModal
        open={showResetPassModal}
        setOpen={setShowResetPassModal}
        call={users.requestClientPassword}
      />
    </MainLayout>
  );
};
